import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { C, cs } from '../styles/common';
import api from '../api';

export default function Financials() {
  const { isDirector }  = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab]   = useState('inventory'); // inventory | cost | scrap | revenue

  const load = useCallback(() => {
    setLoading(true);
    api.get('/api/v1/financials/summary')
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!isDirector) {
    return (
      <div style={{ ...cs.page, ...cs.center }}>
        <p style={{ color: C.muted }}>Director access required.</p>
      </div>
    );
  }

  const fmt = (n) => n != null ? `€${parseFloat(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—';

  return (
    <div style={cs.page}>
      {/* Header */}
      <div style={{ ...cs.pageHeader, marginBottom: '20px' }}>
        <div>
          <h1 style={cs.pageTitle}>Financials</h1>
          <p style={cs.pageSub}>30-day snapshot — inventory value, production costs, scrap, and revenue</p>
        </div>
        <button style={{ ...cs.btnSecondary, display: 'flex', alignItems: 'center', gap: '6px' }} onClick={load}>
          <i className="fa-solid fa-rotate-right" />
          Refresh
        </button>
      </div>

      {/* Stats bar */}
      {!loading && data && (
        <div style={s.statsBar}>
          <Stat label="Inventory value"  value={fmt(data.inventory?.total_eur)} highlight />
          <Stat label="Revenue (30 d)"   value={fmt(data.revenue?.total_eur)} highlight />
          <Stat label="Scrap cost (30 d)" value={fmt(data.scrap?.total_eur)} />
          <Stat label="Parts tracked"    value={data.cost_per_part?.length ?? '—'} />
        </div>
      )}

      {/* Tabs */}
      <div style={s.tabs}>
        {[
          { key: 'inventory', label: 'Inventory value' },
          { key: 'cost',      label: 'Cost per part' },
          { key: 'scrap',     label: 'Scrap costs' },
          { key: 'revenue',   label: 'Revenue' },
        ].map(t => (
          <button key={t.key}
            style={{ ...s.tab, ...(tab === t.key ? s.tabActive : {}) }}
            onClick={() => setTab(t.key)}>
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={cs.center}><p style={{ color: C.muted }}>Loading…</p></div>
      ) : !data ? (
        <div style={cs.center}><p style={{ color: C.muted }}>Failed to load financial data.</p></div>
      ) : (
        <>
          {tab === 'inventory' && <InventoryTab data={data.inventory} fmt={fmt} />}
          {tab === 'cost'      && <CostTab      data={data.cost_per_part} fmt={fmt} />}
          {tab === 'scrap'     && <ScrapTab     data={data.scrap} fmt={fmt} />}
          {tab === 'revenue'   && <RevenueTab   data={data.revenue} fmt={fmt} />}
        </>
      )}
    </div>
  );
}

// ── Tabs ──────────────────────────────────────────────────────────────────────

function InventoryTab({ data, fmt }) {
  if (!data?.lines?.length) return <Empty text="No materials with stock and cost data." />;
  return (
    <div style={cs.tableWrap}>
      <div style={{ ...cs.tableHeader, ...s.invGrid }}>
        <span>Material</span><span style={s.right}>Stock (kg)</span>
        <span style={s.right}>Cost/kg</span><span style={s.right}>Value</span>
      </div>
      {data.lines.map(l => (
        <div key={l.id} style={{ ...cs.tableRow, ...s.invGrid }}>
          <span style={{ color: C.text }}>{l.name}</span>
          <span style={{ ...s.right, color: C.muted }}>{parseFloat(l.stock_kg).toLocaleString()} kg</span>
          <span style={{ ...s.right, color: C.muted }}>{fmt(l.cost_per_kg)}</span>
          <span style={{ ...s.right, color: C.teal, fontWeight: '700' }}>{fmt(l.value_eur)}</span>
        </div>
      ))}
      <div style={{ ...cs.tableRow, ...s.invGrid, background: C.surfaceAlt }}>
        <span style={{ fontWeight: '700', color: C.text }}>Total</span>
        <span /><span />
        <span style={{ ...s.right, color: C.teal, fontWeight: '700', fontSize: '15px' }}>{fmt(data.total_eur)}</span>
      </div>
    </div>
  );
}

function CostTab({ data, fmt }) {
  if (!data?.length) return <Empty text="No cost data — add machine rates and run jobs to calculate." />;
  return (
    <div style={cs.tableWrap}>
      <div style={{ ...cs.tableHeader, ...s.costGrid }}>
        <span>Part</span><span style={s.right}>Jobs</span>
        <span style={s.right}>Parts made</span><span style={s.right}>Cost/part</span>
      </div>
      {data.map(p => (
        <div key={p.part_id} style={{ ...cs.tableRow, ...s.costGrid }}>
          <div>
            <div style={{ color: C.text }}>{p.name}</div>
            {p.part_number && <div style={{ fontSize: '11px', color: C.muted }}>{p.part_number}</div>}
          </div>
          <span style={{ ...s.right, color: C.muted }}>{p.job_count}</span>
          <span style={{ ...s.right, color: C.muted }}>{p.total_parts.toLocaleString()}</span>
          <span style={{ ...s.right, color: C.teal, fontWeight: '700' }}>{fmt(p.cost_per_part)}</span>
        </div>
      ))}
    </div>
  );
}

function ScrapTab({ data, fmt }) {
  if (!data?.by_part?.length) return <Empty text="No scrap recorded in the last 30 days." />;
  return (
    <>
      <div style={{ ...s.statsBar, marginBottom: '16px' }}>
        <Stat label="Total scrap cost" value={fmt(data.total_eur)} />
      </div>
      <div style={cs.tableWrap}>
        <div style={{ ...cs.tableHeader, ...s.scrapGrid }}>
          <span>Part</span><span style={s.right}>Scrap count</span><span style={s.right}>Est. cost</span>
        </div>
        {data.by_part.map(p => (
          <div key={p.part_id} style={{ ...cs.tableRow, ...s.scrapGrid }}>
            <div>
              <div style={{ color: C.text }}>{p.name}</div>
              {p.part_number && <div style={{ fontSize: '11px', color: C.muted }}>{p.part_number}</div>}
            </div>
            <span style={{ ...s.right, color: C.muted }}>{p.scrap_count.toLocaleString()} pcs</span>
            <span style={{ ...s.right, color: '#f87171', fontWeight: '700' }}>{fmt(p.scrap_cost_eur)}</span>
          </div>
        ))}
      </div>
    </>
  );
}

function RevenueTab({ data, fmt }) {
  if (!data?.by_part?.length) return <Empty text="No order lines with unit prices in the last 30 days." />;
  return (
    <>
      <div style={{ ...s.statsBar, marginBottom: '16px' }}>
        <Stat label="Total revenue (30 d)" value={fmt(data.total_eur)} highlight />
      </div>
      <div style={cs.tableWrap}>
        <div style={{ ...cs.tableHeader, ...s.revGrid }}>
          <span>Part</span><span style={s.right}>Qty ordered</span><span style={s.right}>Revenue</span>
        </div>
        {data.by_part.map(p => (
          <div key={p.part_id} style={{ ...cs.tableRow, ...s.revGrid }}>
            <div>
              <div style={{ color: C.text }}>{p.name}</div>
              {p.part_number && <div style={{ fontSize: '11px', color: C.muted }}>{p.part_number}</div>}
            </div>
            <span style={{ ...s.right, color: C.muted }}>{p.quantity.toLocaleString()} pcs</span>
            <span style={{ ...s.right, color: C.teal, fontWeight: '700' }}>{fmt(p.revenue_eur)}</span>
          </div>
        ))}
      </div>
    </>
  );
}

// ── Shared helpers ─────────────────────────────────────────────────────────────

function Stat({ label, value, highlight }) {
  return (
    <div style={s.stat}>
      <span style={{ ...s.statValue, ...(highlight ? { color: C.teal } : {}) }}>{value}</span>
      <span style={s.statLabel}>{label}</span>
    </div>
  );
}

function Empty({ text }) {
  return (
    <div style={{ ...cs.center, flexDirection: 'column', gap: '12px', paddingTop: '60px' }}>
      <i className="fa-solid fa-chart-pie" style={{ fontSize: '32px', color: C.mutedDark }} />
      <p style={{ color: C.muted, margin: 0 }}>{text}</p>
    </div>
  );
}

// ── Styles ─────────────────────────────────────────────────────────────────────

const s = {
  statsBar: {
    display: 'flex', gap: '1px', background: C.border,
    borderRadius: '8px', overflow: 'hidden', marginBottom: '20px',
  },
  stat: {
    flex: 1, background: C.surface, padding: '12px 16px',
    display: 'flex', flexDirection: 'column', gap: '2px',
  },
  statValue: { fontSize: '20px', fontWeight: '700', color: C.text },
  statLabel: { fontSize: '11px', color: C.muted, textTransform: 'uppercase', letterSpacing: '0.05em' },

  tabs: { display: 'flex', borderBottom: `1px solid ${C.border}`, marginBottom: '20px' },
  tab: {
    background: 'none', border: 'none', color: C.muted, fontSize: '13px',
    fontWeight: '500', padding: '8px 18px', cursor: 'pointer',
    borderBottom: '2px solid transparent', marginBottom: '-1px',
    fontFamily: 'system-ui, sans-serif',
  },
  tabActive: { color: C.text, borderBottom: `2px solid ${C.teal}` },

  right: { textAlign: 'right' },
  invGrid:  { gridTemplateColumns: '1fr 120px 100px 120px' },
  costGrid: { gridTemplateColumns: '1fr 80px 120px 100px' },
  scrapGrid: { gridTemplateColumns: '1fr 120px 100px' },
  revGrid:  { gridTemplateColumns: '1fr 120px 120px' },
};
